import { useState, useEffect, useRef } from "react"
import logo from "@/assets/images/zoe-fc-logo.jpeg"

const SESSION_KEY = "zoe-intro-shown"

// Set to true to always show splash for testing
const DEBUG_ALWAYS_SHOW = true

interface IntroSplashProps {
  onComplete: () => void
}

export function IntroSplash({ onComplete }: IntroSplashProps) {
  const [phase, setPhase] = useState<"fade-in" | "hold" | "transition" | "done">("fade-in")
  const logoRef = useRef<HTMLDivElement>(null)
  const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0, scale: 1 })

  useEffect(() => {
    // Check if already shown this session (skip in debug mode)
    if (!DEBUG_ALWAYS_SHOW && sessionStorage.getItem(SESSION_KEY)) {
      onComplete()
      return
    }

    // Calculate the navbar logo position for the transition
    const calculateTarget = () => {
      // Header logo is 64px (w-16 h-16), positioned in the navbar
      // We need to calculate where it will end up
      const headerHeight = 80 // h-20 = 5rem = 80px
      const logoSize = 64
      const navLogoX = 32 + logoSize / 2 // px from left + half logo width
      const navLogoY = headerHeight / 2 // Centered in header

      const viewportCenterX = window.innerWidth / 2
      const viewportCenterY = window.innerHeight / 2

      // Calculate translation needed
      const translateX = navLogoX - viewportCenterX
      const translateY = navLogoY - viewportCenterY
      const splashLogoSize = 320 // w-80 = 20rem = 320px
      const scale = logoSize / splashLogoSize

      setTargetPosition({ x: translateX, y: translateY, scale })
    }

    calculateTarget()
    window.addEventListener("resize", calculateTarget)

    // Phase timing
    const fadeInTimer = setTimeout(() => setPhase("hold"), 600)
    const holdTimer = setTimeout(() => setPhase("transition"), 2000)
    const completeTimer = setTimeout(() => {
      setPhase("done")
      if (!DEBUG_ALWAYS_SHOW) {
        sessionStorage.setItem(SESSION_KEY, "true")
      }
      onComplete()
    }, 2800)

    return () => {
      window.removeEventListener("resize", calculateTarget)
      clearTimeout(fadeInTimer)
      clearTimeout(holdTimer)
      clearTimeout(completeTimer)
    }
  }, [onComplete])

  // Skip render if already shown
  if (phase === "done") return null

  return (
    <div
      className={`fixed inset-0 z-100 flex items-center justify-center bg-navy transition-opacity duration-700 ${
        phase === "transition" ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      <div
        ref={logoRef}
        className={`transition-all duration-700 ease-out ${
          phase === "fade-in" ? "opacity-0 scale-95" : "opacity-100 scale-100"
        }`}
        style={
          phase === "transition"
            ? {
                transform: `translate(${targetPosition.x}px, ${targetPosition.y}px) scale(${targetPosition.scale})`,
                opacity: 0,
              }
            : undefined
        }
      >
        <div className="w-80 h-80 rounded-lg overflow-hidden bg-navy shadow-2xl">
          <img
            src={logo}
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  )
}

export function useIntroSplash() {
  const [showSplash, setShowSplash] = useState(() => {
    // Always show in debug mode, otherwise check session
    if (DEBUG_ALWAYS_SHOW) return true
    if (typeof window !== "undefined") {
      return !sessionStorage.getItem(SESSION_KEY)
    }
    return false
  })
  const [splashComplete, setSplashComplete] = useState(() => {
    if (DEBUG_ALWAYS_SHOW) return false
    if (typeof window !== "undefined") {
      return !!sessionStorage.getItem(SESSION_KEY)
    }
    return true
  })

  const handleSplashComplete = () => {
    setShowSplash(false)
    setSplashComplete(true)
  }

  return {
    showSplash,
    splashComplete,
    SplashComponent: showSplash ? (
      <IntroSplash onComplete={handleSplashComplete} />
    ) : null,
  }
}
